import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const NewsletterForm = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setStatus("loading");
    setErrorMsg("");

    const { error } = await supabase
      .from("newsletter_subscribers")
      .insert({ email: email.trim().toLowerCase() });

    if (error) {
      if (error.code === "23505") {
        setStatus("success");
      } else {
        setStatus("error");
        setErrorMsg("Something went wrong. Please try again.");
      }
    } else {
      setStatus("success");
      const normalizedEmail = email.trim().toLowerCase();
      // Fire confirmation email (best-effort, don't block UI)
      const subId = crypto.randomUUID();
      supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "newsletter-confirmation",
          recipientEmail: normalizedEmail,
          idempotencyKey: `newsletter-confirm-${subId}`,
        },
      }).catch(() => {});
      // Notify team
      supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "newsletter-notification",
          idempotencyKey: `newsletter-notify-${subId}`,
          templateData: { subscriberEmail: normalizedEmail },
        },
      }).catch(() => {});
    }

    setEmail("");
  };

  if (status === "success") {
    return (
      <p className="text-sm font-medium text-surface-inverse-foreground pt-4" role="status">
        You're subscribed — thank you!
      </p>
    );
  }

  return (
    <div className="pt-4">
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-3 max-w-md mx-auto sm:flex-row sm:items-end">
        <div className="min-w-0 flex-1 text-left">
          <label htmlFor="newsletter-email" className="mb-1.5 block px-2 text-xs font-medium text-surface-inverse-foreground">
            Work address
          </label>
          <input
            id="newsletter-email"
            type="email"
            required
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-describedby={status === "error" ? "newsletter-email-help newsletter-error" : "newsletter-email-help"}
            aria-invalid={status === "error" ? true : undefined}
            className="w-full rounded-full border border-white/20 bg-white/5 px-5 py-2.5 text-sm text-surface-inverse-foreground placeholder:text-white/45 focus:outline-none focus:border-primary/70 transition-colors"
          />
          <p id="newsletter-email-help" className="mt-1.5 px-2 text-xs text-surface-inverse-muted">
            For product updates only.
          </p>
        </div>
        <button
          type="submit"
          disabled={status === "loading"}
          className="glow-button rounded-full bg-primary text-white px-6 py-2.5 text-sm font-semibold tracking-wide disabled:opacity-50"
        >
          {status === "loading" ? "…" : "Subscribe"}
        </button>
      </form>
      {status === "error" && (
        <p id="newsletter-error" role="alert" className="text-sm text-red-300 mt-2">{errorMsg}</p>
      )}
    </div>
  );
};

export default NewsletterForm;
